import { Capacitor } from '@capacitor/core';
import { dansLesLimites, type LimitesDuSpot } from '@sirocco/shared';
import { langue, t } from '../i18n';

/**
 * Trouver une vraie adresse, et savoir y mener.
 *
 * Le lieu d'un evenement etait un texte libre : « chez moi », « le parc »,
 * une faute de frappe. Celui qui s'y rend n'a alors rien a ouvrir, et
 * l'organisateur ne le sait pas. On demande donc une adresse que
 * quelqu'un d'autre puisse retrouver, et l'on garde ses coordonnees avec
 * elle.
 *
 * # Le service
 *
 * Photon, un annuaire libre et sans cle, bati sur les donnees
 * d'OpenStreetMap. Le meme partout : web, iPhone et Android partagent ce
 * build, et un service demandant une cle par plateforme aurait fait trois
 * chemins la ou il en faut un.
 *
 * On interrogeait Nominatim, qui puise a la meme source mais cherche
 * autrement : il attend une adresse ecrite en entier. Photon est fait
 * pour la saisie au fil des lettres — il pardonne les fautes, les mots
 * dans le desordre, et trouve les commerces par leur nom. Un « Tasty
 * Crousty » sort chez lui des les premieres frappes.
 *
 * On attend malgre tout que la frappe s'arrete avant d'interroger, et
 * l'on ne cherche qu'a partir de trois lettres : par egard pour un
 * service libre, et parce qu'une adresse ne se reconnait pas a deux
 * lettres de toute facon.
 */

export interface Adresse {
  /** Ce qui s'affiche, et ce qu'on garde : « 12 rue Yves Saint Laurent ». */
  libelle: string;
  lat: number;
  lon: number;
}

const ANNUAIRE = 'https://photon.komoot.io/api/';

/** Ce qui n'a pas pu etre demande : l'ecran le dit plutot que de se taire. */
export class AnnuaireMuet extends Error {}

/**
 * Chercher une adresse, ou qu'elle soit.
 *
 * La question part telle qu'elle est tapee, sans ville ni pays ajoutes.
 * On les joignait au debut, pour que « Koutoubia » ne renvoie pas des
 * resultats aux quatre coins du monde — mais cela enfermait la recherche
 * dans la ville du spot, alors qu'une sortie se passe souvent autour :
 * une plage a une heure de route, un village voisin, une autre ville pour
 * le week-end.
 *
 * Le libelle rendu porte la ville et la region, donc on voit tout de
 * suite lequel des « Saint-Martin » on a sous les yeux. C'est a celui qui
 * cherche de preciser, et il le fait naturellement des que le premier
 * resultat n'est pas le bon.
 */
export async function chercherUneAdresse(
  quete: string,
  signal?: AbortSignal,
  /*
    Le territoire du spot, et son centre. L'annuaire ne cherche que dans
    la boite, et sert d'abord ce qui est pres du centre : a Paris, la rue
    de Paris passe avant celle de Lyon qui porte le meme nom.
  */
  cadre: { limites?: LimitesDuSpot | null; pres?: { lat: number; lon: number } | null } = {},
): Promise<Adresse[]> {
  const mot = quete.trim();
  if (mot.length < 3) return [];
  const { limites, pres } = cadre;

  /*
    Les noms de lieux dans la langue du membre : « Londres » ou « London ».
    L'annuaire ne parle que francais, anglais et allemand : pour une
    autre langue, les noms locaux, qui se lisent partout.
  */
  const parametres = new URLSearchParams({
    q: mot,
    limit: '8',
    ...(langue === 'fr' || langue === 'en' ? { lang: langue } : {}),
    ...(limites ? { bbox: `${limites.ouest},${limites.sud},${limites.est},${limites.nord}` } : {}),
    ...(pres ? { lat: String(pres.lat), lon: String(pres.lon) } : {}),
  });

  const reponse = await fetch(`${ANNUAIRE}?${parametres}`, { signal });
  if (!reponse.ok) {
    /* On ne rend pas une liste vide : « aucune adresse » et « je n'ai pas
       pu demander » ne se disent pas pareil a celui qui cherche, et l'un
       se corrige en retapant quand l'autre demande d'attendre. */
    throw new AnnuaireMuet(`annuaire injoignable (${reponse.status})`);
  }

  /* La boite deborde sur les voisins : le pays tranche, et la boite apres lui. */
  const dansLePays = (proprietes: Record<string, string>, point: { lat: number; lon: number }) =>
    !limites ||
    ((proprietes.countrycode ?? '').toUpperCase() === limites.pays && dansLesLimites(point, limites));

  const brut = (await reponse.json()) as {
    features?: Array<{
      geometry?: { coordinates?: [number, number] };
      properties?: Record<string, string>;
    }>;
  };

  const vues = new Set<string>();

  return (brut.features ?? [])
    .filter((trait) => {
      const [lon, lat] = trait.geometry?.coordinates ?? [NaN, NaN];
      return dansLePays(trait.properties ?? {}, { lat, lon });
    })
    .map((trait) => {
      const [lon, lat] = trait.geometry?.coordinates ?? [NaN, NaN];
      return { libelle: composer(trait.properties ?? {}), lat, lon };
    })
    .filter((a) => a.libelle !== '' && Number.isFinite(a.lat) && Number.isFinite(a.lon))
    .filter((a) => {
      /* Un meme lieu revient plusieurs fois quand OpenStreetMap le porte
         sous plusieurs formes — le point, son enceinte, son entree. Trois
         lignes identiques donneraient a croire que la liste est cassee. */
      const empreinte = `${a.libelle}|${a.lat.toFixed(3)}|${a.lon.toFixed(3)}`;
      if (vues.has(empreinte)) return false;
      vues.add(empreinte);
      return true;
    })
    .slice(0, 6);
}

/**
 * Le libelle d'un resultat, assemble.
 *
 * Photon rend des morceaux plutot qu'une phrase : le nom du lieu quand il
 * en a un, la rue et son numero, la ville, le pays. On les remet dans
 * l'ordre ou on les lit, sans repeter ce qui vaut deux fois — un commerce
 * porte parfois le nom de sa rue, et le voir ecrit deux fois ferait
 * douter du resultat.
 *
 * Le pays est garde : la recherche va partout, et sans lui deux
 * « Saint-Martin » se ressemblent trop pour qu'on choisisse.
 */
function composer(p: Record<string, string>): string {
  const rue = [p.housenumber, p.street].filter(Boolean).join(' ');
  const morceaux = [p.name, rue, p.city ?? p.county, p.country].filter(Boolean);
  return [...new Set(morceaux)].join(', ');
}

/**
 * Les applications capables d'y mener.
 *
 * Trois liens, et non un seul : personne n'utilise la meme. Plans est
 * toujours la sur un iPhone, Google Maps et Waze seulement si on les a —
 * mais leurs liens web basculent vers l'application quand elle existe, et
 * ouvrent le site sinon. Aucun ne peut donc echouer.
 */
export function itineraires(adresse: string, lat: number | null, lon: number | null) {
  const point = lat !== null && lon !== null ? `${lat},${lon}` : '';
  const cible = point || adresse;
  const natif = Capacitor.isNativePlatform();
  return [
    {
      /*
        Le vrai logo de chaque application.

        On avait d'abord mis des signes approchants — une carte pliee, un
        volant — faute de pouvoir redessiner des marques deposees de
        memoire. Les fichiers ont ete fournis : on reconnait desormais
        l'application avant d'avoir lu son nom, ce qu'aucun a-peu-pres ne
        permettait.
      */
      logo: '/logos/plans.png',
      nom: t.composants.itineraire.plans,
      lien: point
        ? `https://maps.apple.com/?daddr=${point}&dirflg=d`
        : `https://maps.apple.com/?q=${encodeURIComponent(adresse)}`,
    },
    {
      logo: '/logos/google.png',
      nom: 'Google Maps',
      lien: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cible)}`,
    },
    {
      logo: '/logos/waze.png',
      nom: 'Waze',
      /*
        Waze par son schema d'application, pas par son lien web.

        Le lien `waze.com/ul` ouvre bien l'application depuis Safari, mais
        pas depuis une vue web : le lien s'ouvre dans un onglet interne, et
        le passage de relais n'a jamais lieu. Plans et Google Maps ne
        souffrent pas de cela — iOS reconnait leurs domaines et prend la
        main avant.

        Le schema, lui, ne ressemble a rien que la vue web sache afficher :
        elle le rend au systeme, qui ouvre l'application. Sur le web, ou
        il n'y a rien a ouvrir, on garde le lien.
      */
      lien: natif
        ? point
          ? `waze://?ll=${point}&navigate=yes`
          : `waze://?q=${encodeURIComponent(adresse)}`
        : point
          ? `https://waze.com/ul?ll=${point}&navigate=yes`
          : `https://waze.com/ul?q=${encodeURIComponent(adresse)}`,
    },
  ];
}
